import { useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import tw from 'twin.macro';
import { ServiceListingStatus } from '../../../__generated__/graphql';
import { Button } from '../../components/button';
import PriceCalendar from '../../components/PriceCalendar';
import {
  Alert,
  Badge,
  Card,
  CardBody,
  CardHeader,
  DD,
  DescriptionList,
  DT,
  InlineRow,
  LoadingBlock,
  Muted,
  PageContainer,
  PageHeader,
  Stack,
  StatCard,
  StatGrid,
} from '../../components/ui';
import {
  usePublishWorkerMutation,
  useUnlistWorkerMutation,
  useWorkerPublishChecklistQuery,
} from '../../services/serviceService/__generated__/queries.generated';
import { CredentialsSection } from './CredentialsSection';
import { EquipmentPhotosEditor } from './EquipmentPhotosEditor';
import { SERVICE_TYPE_LABELS, warningText } from './priceTables';
import { serviceErrorMessage, workerPublishProblemsOf } from './serviceErrors';
import {
  PROVIDER_WORKERS_PATH,
  workerAccountsPath,
  workerEditPath,
  workerPricingPath,
} from './servicePaths';
import { useWorkerEditor } from './useWorkerEditor';
import { PublishChecklist } from './PublishChecklist';
import { WorkerSectionFields } from './WorkerSections';
import {
  isDraftStatus,
  LISTING_STATUS_LABELS,
  onTimeText,
  percentText,
  SECTION_FIELDS,
  SUPPLY_LABELS,
  WEEKDAYS,
  WEEKDAY_LABELS,
  type WizardStepId,
  type WorkerSection,
} from './workerForm';
import { WorkerStrikesCard } from './WorkerStrikesCard';

import { distanceWord } from '../../utils/serviceUnits';
import { asDistanceUnit } from '../../utils/units';
const Columns = styled.div`
  ${tw`grid grid-cols-1 gap-6 lg:grid-cols-3`}
`;

const Main = styled.div`
  ${tw`flex flex-col gap-6 lg:col-span-2 min-w-0`}
`;

const Side = styled.div`
  ${tw`flex flex-col gap-6 min-w-0`}
`;

const SECTION_TITLES: Record<WorkerSection, string> = {
  profile: 'Name and bio',
  area: 'Service area',
  schedule: 'Hours, buffer and notice',
  supplies: 'Supplies',
  booking: 'How customers book',
};

/**
 * Manage one worker (spec 019 W7): every section edits on its own card, the
 * calendar blocks times on the worker's `carId` (no prices: jobs are priced
 * by the price page), and the worker is listed or unlisted here.
 */
export default function WorkerManagePage() {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const editor = useWorkerEditor(id);
  const { worker, form, errors, change, reset, save, saving } = editor;
  const [editing, setEditing] = useState<WorkerSection | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [publishWorker, { loading: publishing }] = usePublishWorkerMutation();
  const [unlistWorker, { loading: unlisting }] = useUnlistWorkerMutation();
  const listed = worker?.settings.listingStatus === ServiceListingStatus.Listed;
  const checklistQuery = useWorkerPublishChecklistQuery({
    variables: { workerCarId: id },
    skip: !worker || listed,
    fetchPolicy: 'network-only',
  });

  if (editor.loading) {
    return (
      <PageContainer width="lg" canvas>
        <LoadingBlock label="Loading the worker" />
      </PageContainer>
    );
  }
  if (!worker) {
    return (
      <PageContainer width="lg" canvas>
        <Alert tone="error" title="Worker not found">
          {serviceErrorMessage(editor.loadError, 'This worker could not be loaded.')}
        </Alert>
      </PageContainer>
    );
  }

  const status = worker.settings.listingStatus;
  const w = worker.worker;
  const location = worker.car.location;

  const onSaveSection = async (section: WorkerSection) => {
    setMessage(null);
    const result = await save(SECTION_FIELDS[section]);
    if (result.ok) {
      toast.success('Saved');
      setEditing(null);
    } else if (result.message) {
      setMessage(result.message);
    }
  };

  const onPublish = async () => {
    setMessage(null);
    try {
      await publishWorker({ variables: { workerCarId: id } });
      toast.success(`${w.displayName} is listed`);
    } catch (error) {
      if (workerPublishProblemsOf(error).length) checklistQuery.refetch();
      setMessage(serviceErrorMessage(error, 'Could not publish'));
    }
  };

  const onUnlist = async () => {
    setMessage(null);
    try {
      await unlistWorker({ variables: { workerCarId: id } });
      toast.success(`${w.displayName} is unlisted`);
    } catch (error) {
      setMessage(serviceErrorMessage(error, 'Could not unlist'));
    }
  };

  const goToStep = (step: WizardStepId) => navigate(workerEditPath(id, step));

  const sectionCard = (section: WorkerSection, summary: ReactNode) => (
    <Card key={section} data-testid={`worker-section-${section}`}>
      <CardHeader
        title={SECTION_TITLES[section]}
        actions={
          editing === section ? undefined : (
            <Button
              type="button"
              theme="secondary"
              size="sm"
              text="Edit"
              isDisabled={editing !== null}
              onClick={() => {
                reset();
                setEditing(section);
              }}
              dataTestId={`worker-edit-${section}`}
            />
          )
        }
      />
      <CardBody>
        {editing === section ? (
          <Stack $gap={4}>
            <WorkerSectionFields
              section={section}
              form={form}
              errors={errors}
              onChange={change}
              locationLabel={location ? `${location.city} ${location.zipCode}` : null}
            />
            <InlineRow>
              <Button
                type="button"
                text="Save"
                onClick={() => onSaveSection(section)}
                isLoading={saving}
                dataTestId={`worker-save-${section}`}
              />
              <Button
                type="button"
                theme="text"
                text="Cancel"
                onClick={() => {
                  reset();
                  setEditing(null);
                }}
              />
            </InlineRow>
          </Stack>
        ) : (
          summary
        )}
      </CardBody>
    </Card>
  );

  const hours = worker.settings.workingHours as Record<string, any> | null;
  const supplies = (Object.keys(SUPPLY_LABELS) as (keyof typeof SUPPLY_LABELS)[]).filter(
    key => w.suppliesProvided[key],
  );

  return (
    <PageContainer width="xl" canvas data-testid="worker-manage">
      <PageHeader
        eyebrow="Worker"
        title={w.displayName}
        description={
          location ? `${location.name} · ${location.city} ${location.zipCode}` : undefined
        }
        backTo={{ to: PROVIDER_WORKERS_PATH, label: 'Workers' }}
        actions={
          <InlineRow>
            <Badge tone={listed ? 'success' : 'warning'} dot data-testid="worker-status">
              {LISTING_STATUS_LABELS[status]}
            </Badge>
            {w.paused && <Badge tone="error">Paused</Badge>}
          </InlineRow>
        }
      />
      {message && (
        <div style={{ marginBottom: 24 }}>
          <Alert tone="error" title="Not saved">
            {message}
          </Alert>
        </div>
      )}
      <Columns>
        <Main>
          {sectionCard(
            'profile',
            <DescriptionList>
              <DT>Name</DT>
              <DD>{w.displayName}</DD>
              <DT>Bio</DT>
              <DD>{w.bio || 'None'}</DD>
              <DT>Experience</DT>
              <DD>{w.yearsExperience != null ? `${w.yearsExperience} years` : 'Not set'}</DD>
              <DT>Languages</DT>
              <DD>{w.languages.length ? w.languages.join(', ') : 'Not set'}</DD>
            </DescriptionList>,
          )}
          <Card data-testid="worker-section-services">
            <CardHeader
              title="Services and prices"
              actions={
                <Button
                  type="button"
                  theme="secondary"
                  size="sm"
                  text="Edit prices"
                  onClick={() => navigate(workerPricingPath(id))}
                />
              }
            />
            <CardBody>
              <Stack $gap={4}>
                <Muted $size="sm">
                  {w.offerings.filter(o => o.isActive).length
                    ? w.offerings
                        .filter(o => o.isActive)
                        .map(o => SERVICE_TYPE_LABELS[o.serviceType])
                        .join(', ')
                    : 'No services offered yet'}
                </Muted>
                {worker.priceWarnings.length > 0 && (
                  <Alert tone="info" title="Low prices">
                    {worker.priceWarnings.length === 1
                      ? warningText(worker.priceWarnings[0])
                      : `${worker.priceWarnings.length} prices work out below the local minimum wage.`}
                  </Alert>
                )}
              </Stack>
            </CardBody>
          </Card>
          {sectionCard(
            'area',
            <DescriptionList>
              <DT>Radius</DT>
              <DD>
                {w.serviceRadiusMiles} {distanceWord(asDistanceUnit(w.distanceUnit))}
              </DD>
              <DT>Extra ZIP codes</DT>
              <DD>
                {worker.settings.serviceZips.length
                  ? worker.settings.serviceZips.join(', ')
                  : 'None'}
              </DD>
              <DT>Past the radius</DT>
              <DD>
                {w.acceptsOutOfRadius
                  ? `$${w.perMileBeyondRadius ?? 0} a ${distanceWord(asDistanceUnit(w.distanceUnit), false)}, up to ${w.maxTravelMiles ?? w.serviceRadiusMiles} ${distanceWord(asDistanceUnit(w.distanceUnit))}`
                  : 'Not taken'}
              </DD>
            </DescriptionList>,
          )}
          {sectionCard(
            'schedule',
            <DescriptionList>
              <DT>Hours</DT>
              <DD>
                {!hours
                  ? "The location's hours"
                  : WEEKDAYS.filter(day => hours[day]?.available)
                      .map(
                        day =>
                          `${WEEKDAY_LABELS[day].slice(0, 3)} ${hours[day].start}–${hours[day].end}`,
                      )
                      .join(', ') || 'Closed every day'}
              </DD>
              <DT>Buffer</DT>
              <DD>{w.bufferHours} hours after each job</DD>
              <DT>Advance notice</DT>
              <DD>{w.advanceNoticeHours} hours</DD>
            </DescriptionList>,
          )}
          <Card data-testid="worker-section-equipment">
            <CardHeader title="Equipment photos" description="At least 3 to publish." />
            <CardBody>
              <EquipmentPhotosEditor worker={worker} />
            </CardBody>
          </Card>
          {sectionCard(
            'supplies',
            <Muted $size="sm">
              {!worker.settings.suppliesSet
                ? 'Not set yet'
                : supplies.length
                  ? `Brings: ${supplies.map(key => SUPPLY_LABELS[key]).join(', ')}`
                  : 'Brings no supplies'}
            </Muted>,
          )}
          {sectionCard(
            'booking',
            <Muted $size="sm">
              {w.instantBook
                ? 'Instant book: jobs are confirmed right away.'
                : `Requests: confirm within ${worker.settings.confirmWindowHours} hours.`}
            </Muted>,
          )}
          <CredentialsSection worker={worker} />
          <Card data-testid="worker-calendar">
            <CardHeader
              title="Block times"
              description="Pick days when this worker cannot take jobs. Booked jobs show as black lines."
            />
            <CardBody>
              <PriceCalendar
                carId={worker.carId}
                priceCalendar={(worker.car.priceCalendar ?? []) as any}
                sectionTitle="Availability"
                modeLabel="daily"
                allowDynamicPricing={false}
                pricingLocked
                timezone={location?.timezone ?? undefined}
              />
            </CardBody>
          </Card>
        </Main>
        <Side>
          <Card>
            <CardHeader title="Listing" />
            <CardBody>
              <Stack $gap={4}>
                {listed ? (
                  <>
                    <Muted $size="sm">Customers can find and book {w.displayName}.</Muted>
                    <Button
                      type="button"
                      theme="secondary"
                      text="Unlist"
                      onClick={onUnlist}
                      isLoading={unlisting}
                      dataTestId="worker-unlist"
                    />
                  </>
                ) : (
                  <>
                    <PublishChecklist
                      problems={checklistQuery.data?.workerPublishChecklist ?? null}
                      warnings={[]}
                      onGoTo={goToStep}
                    />
                    <Button
                      type="button"
                      text={isDraftStatus(status) ? 'Publish' : 'Publish again'}
                      onClick={onPublish}
                      isLoading={publishing}
                      isDisabled={(checklistQuery.data?.workerPublishChecklist?.length ?? 1) > 0}
                      dataTestId="worker-publish"
                    />
                  </>
                )}
              </Stack>
            </CardBody>
          </Card>
          <StatGrid $cols={2}>
            <StatCard label="On time" value={onTimeText(w.onTimeRate, w.completedJobCount)} />
            <StatCard label="Cancelled" value={percentText(w.cancellationRate)} />
            <StatCard label="Jobs done" value={w.completedJobCount} />
            <StatCard
              label="Rating"
              value={w.averageRating ? w.averageRating.toFixed(1) : 'None'}
              hint={w.totalRatings ? `${w.totalRatings} reviews` : undefined}
            />
          </StatGrid>
          {w.paused && (
            <Alert tone="error" title="Paused">
              {worker.settings.pauseReason ?? 'This worker is paused and cannot be booked.'}
            </Alert>
          )}
          <WorkerStrikesCard workerCarId={id} />
          <Card>
            <CardHeader title="Logins" description="Who signs in as this worker to do the jobs." />
            <CardBody>
              <Button
                type="button"
                theme="secondary"
                size="sm"
                text="Manage logins"
                onClick={() => navigate(workerAccountsPath(id))}
              />
            </CardBody>
          </Card>
        </Side>
      </Columns>
    </PageContainer>
  );
}
